import { Command as CommandPrimitive } from 'cmdk'
import { X } from 'lucide-react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { InputWrapper } from '../base/input-wrapper'
import { BaseInputProps } from '../base/types'
import { Option } from './utils'

interface AutocompleteProps extends BaseInputProps {
  value?: Option
  options: Option[]
  placeholder?: string
  emptyIndicator?: React.ReactNode
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  onChange?: (option: Option | undefined) => void
  disabled?: boolean
  className?: string
  ignoreDropdownStyles?: boolean
  onBlur?: () => void
  showClearButton?: boolean
}

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder,
      options = [],
      emptyIndicator,
      startAdornment,
      endAdornment,
      disabled,
      className,
      ignoreDropdownStyles = false,
      wrapperClassName,
      errorText,
      helperText,
      label,
      required,
      showDirtyIndicator,
      tooltip,
      showClearButton = true,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = useState(value?.label || '')
    const [position, setPosition] = useState<'top' | 'bottom'>('bottom')

    // Update input value when the selected value changes
    useEffect(() => {
      setInputValue(value?.label || '')
    }, [value])

    // Calculate position when the dropdown opens
    useEffect(() => {
      if (!open) return

      const calculatePosition = () => {
        const container = containerRef.current
        const list = listRef.current
        if (!container || !list) return

        const containerRect = container.getBoundingClientRect()
        const listHeight = list.offsetHeight
        const windowHeight = window.innerHeight
        const spaceBelow = windowHeight - containerRect.bottom
        const spaceAbove = containerRect.top

        // If there's not enough space below and more space above, flip to top
        if (spaceBelow < listHeight && spaceAbove > spaceBelow) {
          setPosition('top')
        } else {
          setPosition('bottom')
        }
      }

      calculatePosition()
      window.addEventListener('scroll', calculatePosition)
      window.addEventListener('resize', calculatePosition)

      return () => {
        window.removeEventListener('scroll', calculatePosition)
        window.removeEventListener('resize', calculatePosition)
      }
    }, [open])

    const handleValueChange = (newValue: string) => {
      // If there is a selected value, do not let the user type in the input
      if (value) {
        return
      }

      setInputValue(newValue)
      // If the input is cleared, also clear the selection
      if (newValue === '') {
        onChange?.(undefined)
      }
      setOpen(true) // Always open dropdown when typing
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow backspace to clear even when there's a selected value
      if (e.key === 'Backspace') {
        if (value && inputValue === value.label) {
          e.preventDefault()
          onChange?.(undefined)
          setInputValue('')
          setOpen(true)
        }
      }
    }

    const handleFocus = () => {
      setOpen(true)
      if (value && inputRef.current) {
        inputRef.current.select()
      }
    }

    const handleBlur = (e: React.FocusEvent) => {
      // Only close if we're not clicking inside the component
      const clickedWithin = containerRef.current?.contains(e.relatedTarget as Node)
      if (!clickedWithin) {
        setInputValue(value?.label || '')
        setOpen(false)
      }
      onBlur?.()
    }

    // Get the positioning styles for the dropdown
    const getDropdownStyles = (): React.CSSProperties => {
      if (ignoreDropdownStyles) {
        return {
          position: 'absolute',
          width: '100%',
          [position === 'top' ? 'bottom' : 'top']:
            position === 'top' ? 'calc(100% + 4px)' : 'calc(100% + 4px)',
          left: 0,
          zIndex: 50,
        }
      }

      const containerRect = containerRef.current?.getBoundingClientRect()
      if (!containerRect) return {}

      return {
        position: 'fixed',
        width: containerRef.current?.offsetWidth,
        left: containerRect.left,
        [position === 'top' ? 'bottom' : 'top']:
          position === 'top' ? window.innerHeight - containerRect.top : containerRect.bottom,
      }
    }

    return (
      <InputWrapper
        errorText={errorText}
        helperText={helperText}
        label={label}
        required={required}
        showDirtyIndicator={showDirtyIndicator}
        tooltip={tooltip}
        wrapperClassName={wrapperClassName}
      >
        <div ref={containerRef} className="relative w-full">
          <Command
            className={cn('overflow-visible bg-transparent', className)}
            filter={(option, search) => {
              // If a value is selected, show all options
              if (value) {
                return 1
              }
              if (option.toLowerCase().includes(search.toLowerCase())) {
                return 1
              }
              return 0
            }}
          >
            <div
              className={cn(
                'group border border-transparent px-3 h-9 text-sm border-gray-4 rounded-sm hover:border-gray-5 focus-within:border-blue-8 focus-within:hover:border-blue-8 focus-within:shadow-input-focus focus-within:outline-none flex items-center',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <div className="flex w-full min-w-0 items-center gap-2">
                {(value?.startIcon || startAdornment) && (
                  <div className="flex flex-shrink-0 items-center">
                    {value?.startIcon || startAdornment}
                  </div>
                )}
                <CommandPrimitive.Input
                  ref={ref || inputRef}
                  className={cn(
                    'flex-1 bg-transparent outline-none text-xs placeholder:text-gray-8 min-w-0 truncate',
                    disabled && 'cursor-not-allowed',
                  )}
                  disabled={disabled}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  onValueChange={handleValueChange}
                  placeholder={placeholder}
                  value={inputValue}
                />
                {(value?.endIcon || endAdornment) && (
                  <div className="flex flex-shrink-0 items-center">
                    {value?.endIcon || endAdornment}
                  </div>
                )}
                {!disabled &&
                  showClearButton &&
                  containerRef.current?.contains(document.activeElement) && (
                    <Button
                      className="flex-shrink-0 p-0"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setInputValue('')
                        onChange?.(undefined)
                        inputRef.current?.focus() // prevents focus loss which causes dropdown to become uncloseable
                      }}
                      size="xs"
                      variant="ghost"
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </Button>
                  )}
              </div>
            </div>

            <div
              ref={listRef}
              className={cn(
                'z-[9999] border rounded-sm bg-popover text-popover-foreground shadow-md outline-none animate-in my-1',
                !open && 'hidden',
              )}
              style={getDropdownStyles()}
            >
              <CommandList>
                {options.length > 0 ? (
                  options.map((option) => {
                    const isSelected = option.value === value?.value
                    return (
                      <CommandItem
                        key={option.value}
                        className={cn(
                          'cursor-pointer text-gray-10 hover:text-gray-12 text-xs flex items-center justify-between',
                          option.disable && 'cursor-not-allowed text-gray-10',
                          isSelected && "bg-gray-2 aria-[selected='true']:bg-gray-3",
                        )}
                        disabled={option.disable}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onSelect={() => {
                          onChange?.(option)
                          setInputValue('')
                          setOpen(false)
                        }}
                        value={option.label}
                      >
                        <div className="flex min-w-0 flex-1 items-center">
                          {option.startIcon && (
                            <span className="me-2 flex flex-shrink-0 items-center">
                              {option.startIcon}
                            </span>
                          )}
                          <span className="truncate">{option.label}</span>
                        </div>
                        {option.endIcon && (
                          <span className="ms-2 flex flex-shrink-0 items-center">
                            {option.endIcon}
                          </span>
                        )}
                      </CommandItem>
                    )
                  })
                ) : (
                  <CommandEmpty>{emptyIndicator}</CommandEmpty>
                )}
              </CommandList>
            </div>
          </Command>
        </div>
      </InputWrapper>
    )
  },
)

Autocomplete.displayName = 'Autocomplete'
